import React, { useMemo } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import type { AuditLogEntry } from '@oolio-group/audit-sdk';
import Icon from '../../Icon/Icon';
import { useTheme } from '../../../hooks/useTheme';
import { makeStyles } from './ActionLogs.styles';

interface Props {
  log: AuditLogEntry;
  onClose: () => void;
}

const ActionLogDetail: React.FC<Props> = ({ log, onClose }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const levelColor =
    log.level === 'error'
      ? colors.negative
      : log.level === 'warn'
      ? colors.focus
      : colors.neutral;

  const details: Array<[string, unknown, string?]> = [
    ['Time', log.ts],
    ['Level', (log.level || '').toUpperCase(), levelColor],
    ['Type', log.tag],
    ['Message', log.msg],
    ...Object.entries(log.context ?? {}).map(
      ([key, value]) => [key, value] as [string, unknown],
    ),
  ];

  return (
    <View style={styles.detail}>
      <View style={styles.detailHeader}>
        <Text style={styles.detailTitle}>Event Details</Text>
        <TouchableOpacity testID="btn-closeDetail" onPress={onClose}>
          <Icon name="times" size={20} color={colors.text} />
        </TouchableOpacity>
      </View>
      <ScrollView style={styles.detailScroll}>
        {details.map(([label, value, color]) => (
          <View style={styles.detailItem} key={label}>
            <Text style={styles.detailLabel}>{label}</Text>
            {typeof value === 'object' && value !== null ? (
              <View style={styles.jsonContainer}>
                <Text style={styles.jsonContent} selectable>
                  {JSON.stringify(value, null, 2)}
                </Text>
              </View>
            ) : (
              <Text
                selectable
                style={[styles.detailValue, !!color && { color }]}
              >
                {String(value)}
              </Text>
            )}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

export default ActionLogDetail;
